import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { InvoiceDA } from '../../services/admin/invoice.service';
import { GlobalSwal } from '../../styles/swal.style';
import { Error } from '../../models/error.model';
import { Invoice } from '../../models/invoice.model';
import { Reminder } from '../../models/reminder.model';
import { legendInvalid, legendIconValidInvalidRestart, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { evaluateLegendOptionalIcon, evaluateLegendRequired } from '../../scripts/validate.legend.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppLegend from '../element/Legend';
import AppLoading from '../Loading';

export interface AppModalInvoiceUpdateReminderProps {
  id_company: string,
  invoice: Invoice | null,
  reminder: Reminder | null,
  setInvoice: Dispatch<SetStateAction<Invoice | null>>
  setReminder: Dispatch<SetStateAction<Reminder | null>>
};

let errorResponse: Error, invoiceResponse: Invoice;

const AppModalInvoiceUpdateReminder: React.FunctionComponent<AppModalInvoiceUpdateReminderProps> = ({id_company, invoice, reminder, setInvoice, setReminder}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [description, setDescription] = useState({value: '', valid: false})
  const [amount, setAmount] = useState({value: '', valid: false})

  const submitInvoiceUpdateReminder = async () => {
    setLoadIndicator('on')

    if (invoice && reminder && description.valid && amount.valid) {
      await InvoiceDA.putInvoiceReminder(id_company, invoice.id, reminder.id, description.value, amount.value).then( (response) => {
        if (response.status === 200) {
          invoiceResponse = response.data
          setInvoice(invoiceResponse)

          GlobalSwal.fire({
            title: lang.labels.successfullyUpdatedReminder,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalInvoiceUpdateReminder()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!description.value.length) legendInvalid('modal-invoice-update-reminder-legend-description-required')
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeDescription = (event: React.ChangeEvent <HTMLFormElement | HTMLTextAreaElement>) => {
    setDescription({...description, value: event.target.value})
  }

  const handleChangeAmount = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setAmount({...amount, value: event.target.value})
  }

  const validateDescription = () => {
    evaluateLegendRequired(expressions.text, description, setDescription, 'modal-invoice-update-reminder-legend-description-valid', 'modal-invoice-update-reminder-legend-description-required')
  }

  const validateAmount = () => {
    evaluateLegendOptionalIcon(expressions.currency, amount, setAmount, 'modal-invoice-update-reminder-input-amount', 'modal-invoice-update-reminder-legend-amount-valid')
  }

  const executeHideModalInvoiceUpdateReminder = () => {
    modalHide('modal-invoice-update-reminder')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendValidInvalidRestart('modal-invoice-update-reminder-legend-description-valid')
    legendValidInvalidRestart('modal-invoice-update-reminder-legend-description-required')
    legendIconValidInvalidRestart('modal-invoice-update-reminder-input-amount', 'modal-invoice-update-reminder-legend-amount-valid')

    setReminder(null)
  }

  useEffect( () => {
    if (reminder) {
      setDescription({value: reminder.description, valid: true})
      setAmount({value: reminder.amount.toString(), valid: true})
    } else {
      setDescription({value: '', valid: false})
      setAmount({value: '', valid: false})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminder])

  return (
    <div id="modal-invoice-update-reminder" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.updateReminder}</h2>
          </div>
          <div className="modal-body">
            { invoice && reminder
              ?
              <div className="form">
                <div className="fv-row mb-3">
                  <label className="form-label required">{lang.labels.description}</label>
                  <TextareaAutosize className="form-control" name="description" minRows={2} maxRows={3} value={description.value} onChange={handleChangeDescription} onKeyUp={validateDescription} onBlur={validateDescription} />
                  <AppLegend component={"modal-invoice-update"} attribute={{check: description.valid, name: "description", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                </div>
                <div className="fv-row">
                  <label className="form-label">{lang.labels.amount}</label>
                  <div className="position-relative">
                    <div className="position-absolute translate-middle-y top-50 start-0 fw-bolder ms-5">{invoice.currency.symbol}</div>  
                    <input id="modal-invoice-update-reminder-input-amount" className="form-control ps-20" type="text" autoComplete="off" name="amount" placeholder="0.00" value={amount.value} onChange={handleChangeAmount} onKeyUp={validateAmount} onBlur={validateAmount} />
                  </div>
                  <div className="form-text text-justify">{lang.labels.ifNoAmountIsEnteredTheValueWillBeZero}</div>
                  <AppLegend component={"modal-invoice-update"} attribute={{check: amount.valid, name: "amount", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                </div>
              </div>
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalInvoiceUpdateReminder}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitInvoiceUpdateReminder}>
              <span className="indicator-label">{lang.labels.update}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalInvoiceUpdateReminder;
